import { Component, ElementRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs';

// PrimeNG
import { ProgressSpinnerModule } from 'primeng/progressspinner';

// Composants
import { BaseComponent } from '../../abstract/base.component';
import { DocumentationContentComponent, DocLinkTarget, opensInNewTab } from '../documentation-content/documentation-content.component';
import { DocumentationTypeBadgeComponent } from '../documentation-type-badge/documentation-type-badge.component';
import { LockedFeatureDialogComponent } from '../../locked-feature-dialog/locked-feature-dialog.component';
import { MessageAction, MessageComponent } from '../../message/message.component';

// Modèles
import { DocArticle, DocSection } from '../../../models/documentation/documentation.types';

/**
 * Lecture d'un article du manuel : titre, chapô, corps, et les deux blocs de
 * fin. Partagé par la page de documentation et la fenêtre d'aide contextuelle,
 * qui ne diffèrent que par ce qu'elles font d'un renvoi — naviguer, ou changer
 * d'article sur place.
 */
@Component({
    selector: 'app-documentation-article-view',
    imports: [
        CommonModule,
        ProgressSpinnerModule,
        DocumentationContentComponent,
        DocumentationTypeBadgeComponent,
        MessageComponent,
    ],
    templateUrl: './documentation-article-view.component.html'
})
export class DocumentationArticleViewComponent extends BaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    /** Renvoi suivi par le lecteur : à l'hôte de décider ce qu'il en fait. */
    @Output() openArticle = new EventEmitter<DocLinkTarget>();

    /** Article chargé — l'hôte y prend le titre de la page ou de la fenêtre. */
    @Output() articleLoaded = new EventEmitter<DocArticle | undefined>();

    public failed = false;

    public readonly discoverFeatureAction: MessageAction = {
        label: 'Découvrir',
        handler: () => this.onClickDiscoverFeature(),
    };

    private _host = inject(ElementRef);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _anchor?: string;
    public get anchor(): string | undefined {
        return this._anchor;
    }
    @Input() public set anchor(value: string | undefined) {
        this._anchor = value;
    }

    private _article?: DocArticle;
    public get article(): DocArticle | undefined {
        return this._article;
    }

    private _articleId?: string;
    public get articleId(): string | undefined {
        return this._articleId;
    }
    @Input() public set articleId(value: string | undefined) {
        this._articleId = value;
        this._load();
    }

    private _content = '';
    public get content(): string {
        return this._content;
    }

    /**
     * Lecture en fenêtre : corps resserré, et pas de durée de lecture — on
     * n'ouvre pas l'aide d'un écran pour arbitrer entre lire maintenant ou plus
     * tard, mais pour se débloquer.
     */
    private _dense = false;
    public get dense(): boolean {
        return this._dense;
    }
    @Input() public set dense(value: boolean) {
        this._dense = value;
    }

    private _next: DocArticle[] = [];
    public get next(): DocArticle[] {
        return this._next;
    }

    private _related: DocArticle[] = [];
    public get related(): DocArticle[] {
        return this._related;
    }

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    /** L'article documente une fonctionnalité absente de l'abonnement du dossier. */
    public get lockedFeature(): boolean {
        const feature = this._article?.feature;
        return !!feature && !this.appService.selectedClient?.hasFeature(feature);
    }

    public get sectionIcon(): string {
        return this._section?.icon ?? '';
    }

    public get sectionTitle(): string {
        return this._section?.title ?? '';
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    /**
     * L'adresse de l'article **dans l'application**, dossier compris : les deux
     * blocs de fin sont de vrais liens, pour que le Ctrl+clic et le clic du
     * milieu y ouvrent un onglet comme sur n'importe quel autre (#984).
     */
    public articleHref(article: DocArticle): string {
        return this.router.serializeUrl(
            this.router.createUrlTree([this.appService.clientUrl('/documentation'), article.id])
        );
    }

    /**
     * Le clic simple, lui, reste une affaire interne : il ouvre l'article dans
     * la page ou dans la fenêtre, selon l'hôte, et ne recharge donc rien.
     */
    public onClickArticle(event: MouseEvent, article: DocArticle): void {
        if (opensInNewTab(event)) {
            return;
        }
        event.preventDefault();
        this.openArticle.emit({ id: article.id });
    }

    public onClickDiscoverFeature(): void {
        this._openLockedFeatureDialog();
    }

    public onNavigate(target: DocLinkTarget): void {
        this.openArticle.emit(target);
    }

    /**
     * Fait venir une section sous les yeux dans l'article **déjà affiché** :
     * c'est ce qu'attend un renvoi vers une autre section du même article, là
     * où changer d'article passe, lui, par un rechargement du corps.
     */
    public scrollToAnchor(anchor: string): void {
        const container = this._scrollContainer();
        const target = document.getElementById(anchor);
        if (!container || !target) {
            return;
        }
        const top = container.scrollTop + target.getBoundingClientRect().top - container.getBoundingClientRect().top;
        container.scrollTo({ top, behavior: 'smooth' });
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _load(): void {
        this._reset();
        if (!this._articleId) {
            return;
        }
        this.loading = true;
        this.subscriptions.add(
            this.documentationService.loadIndex$().subscribe({
                next: () => this._loadContent(),
                error: () => {
                    this.loading = false;
                    this.failed = true;
                },
            })
        );
    }

    private _loadContent(): void {
        this._article = this._articleId ? this.documentationService.article(this._articleId) : undefined;
        this.articleLoaded.emit(this._article);
        if (!this._article) {
            this.loading = false;
            this.failed = true;
            return;
        }
        this._next = this.documentationService.articles(this._article.readNext ?? []);
        this._related = this.documentationService.articles(this._article.related ?? []);
        this.subscriptions.add(
            this.documentationService.loadContent$(this._article.id)
                .pipe(finalize(() => this.loading = false))
                .subscribe({
                    next: (html) => {
                        this._content = html;
                        this._scrollToStart();
                    },
                    error: () => this.failed = true,
                })
        );
    }

    /** Section de l'article — son titre et son icône alimentent le bandeau de méta. */
    private get _section(): DocSection | undefined {
        return this.documentationService.sections.find((section) => section.key === this._article?.section);
    }

    private _openLockedFeatureDialog(): void {
        this.openDialog(LockedFeatureDialogComponent, 'LOCKED_FEATURE', {
            featureCode: this._article?.feature,
            subscription: this.appService.selectedClient?.subscription,
        });
    }

    private _reset(): void {
        this.failed = false;
        this._article = undefined;
        this._content = '';
        this._next = [];
        this._related = [];
    }

    /**
     * Le conteneur qui défile autour de l'article : la colonne de la page, ou le
     * corps de la fenêtre. On le cherche au lieu de s'en remettre à
     * `scrollIntoView`, qui remonte *tous* les ancêtres défilants — et fait donc
     * bouger l'écran resté derrière la fenêtre d'aide.
     */
    private _scrollContainer(): HTMLElement | undefined {
        let element: HTMLElement | null = this._host.nativeElement.parentElement;
        while (element) {
            const overflow = getComputedStyle(element).overflowY;
            if ((overflow === 'auto' || overflow === 'scroll') && element.scrollHeight > element.clientHeight) {
                return element;
            }
            element = element.parentElement;
        }
        return undefined;
    }

    /**
     * Début de lecture : l'ancre visée si le renvoi en désignait une, le haut de
     * l'article sinon. Sans cela, on arriverait sur un article à la hauteur où
     * on avait laissé le précédent.
     */
    private _scrollToStart(): void {
        const anchor = this._anchor;
        setTimeout(() => {
            if (anchor && document.getElementById(anchor)) {
                this.scrollToAnchor(anchor);
                return;
            }
            this._scrollContainer()?.scrollTo({ top: 0, behavior: 'auto' });
        }, 1);
    }
}
